import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { Alert, Button, Label, ReactTablev6 as Table, Tooltip } from '@cimpress/react-components';
import axios from 'axios';
import { getI18nInstance } from '../locales/i18n';
import { BatonEnv, BatonStatus, TransmissionMethodKeys, getBatons } from '@cimpress-technology/baton-client';
import { PromoStandardsTransmissionDetails } from './methods/promoStandard/promoStandardsTransmissionDetails';
import { EmailTransmissionDetails } from './methods/email/emailTransmissionDetails';
import { formatUserDate } from '../utils/formatDate';
import IconArrowLeftAlt from '@cimpress-technology/react-streamline-icons/lib/IconArrowLeftAlt';
import { cssBack, cssDetailsSection, detailsHeader, marginBottom25 } from '../utils/styles';
import { Spinner } from '@cimpress/react-components/lib/shapes';
import { FileTransferTransmissionDetails } from './methods/fileTransfer/fileTransferTransmissionDetails';
import { BatonTransmissionDetails } from './common/batonTransmissionDetails';
import { EmailStatus } from './methods/email/models';
import { ApiPushTransmissionDetails } from './methods/apiPush/apiPushTransmissionDetails';
import { SiteFlowTransmissionDetails } from './methods/siteFlow/siteFlowTransmissionDetails';
import { ApiPollTransmissionDetails } from './methods/apiPoll/apiPollTransmissionDetails';
import { mutedTextCss } from '../integrationConfiguration/style';
import { getEnvBasedUrl, ServiceName } from '../utils/environment';
import { ReTransmit } from './common/reTransmit';
import { getAllV0BatonsForItem } from '../clients/batonClient';
import { BatonV0TransmissionDetails } from './components/batonV0TransmissionDetails';
function isV0Baton(obj) {
    // May be not a good way of identifying the baton type
    return 'key' in obj;
}
export const TransmissionsList = ({ accessToken, itemDetails, env = 'prd', displayTimezone, language }) => {
    const t = getI18nInstance().getFixedT(language);
    const [context, setContext] = useState({
        batons: [],
        batonsV0: [],
        batonToFilter: null,
        transmissionConfigurationsMap: {},
        error: { showError: false, errorMessage: '', batonTransmissionDetails: null },
        showDetails: false,
        isLoading: false
    });
    useEffect(() => {
        if (accessToken) {
            getInitialData();
        }
    }, [accessToken, itemDetails]);
    const onError = ({ showError, errorMessage, batonTransmissionDetails }) => {
        setContext(Object.assign(Object.assign({}, context), { error: { showError, errorMessage, batonTransmissionDetails } }));
    };
    const supplierIntegrationEnvUrl = getEnvBasedUrl(ServiceName.SupplierIntegration, env);
    const hydrateBatonsWithExecutionDetails = async (batons) => {
        const batonsWithExecution = [];
        const promises = batons.map(async (b) => {
            var _a, _b, _c, _d;
            try {
                const transmissionResult = (_a = b._embedded) === null || _a === void 0 ? void 0 : _a.transmissionExecutionDetails;
                // For now only hydrate email batons to know the current execution status as they are transmitted async
                if (b.transmissionMethodKey === TransmissionMethodKeys.Email) {
                    if (transmissionResult === null || transmissionResult === void 0 ? void 0 : transmissionResult._links) {
                        const response = await axios.get((_c = (_b = transmissionResult._links) === null || _b === void 0 ? void 0 : _b.transmissionExecutionResult) === null || _c === void 0 ? void 0 : _c.href, { headers: { Authorization: `Bearer ${accessToken}` } });
                        batonsWithExecution.push(Object.assign(Object.assign({}, b), { executionDetails: (_d = response.data) === null || _d === void 0 ? void 0 : _d._embedded.executions }));
                    }
                }
                else {
                    batonsWithExecution.push(b);
                }
            }
            catch (error) {
                batonsWithExecution.push(b);
            }
        });
        await Promise.all(promises);
        return batonsWithExecution;
    };
    const hydrateBatonsWithTransmissionDetails = async (hydratedBatons) => {
        const batonsWithTransmissionDetails = [];
        const promises = hydratedBatons.map(async (baton) => {
            const transmissionConfigurationHref = baton._links.transmissionConfiguration.href;
            try {
                if (transmissionConfigurationHref) {
                    const response = await axios.get(transmissionConfigurationHref, { headers: { Authorization: `Bearer ${accessToken}` } });
                    batonsWithTransmissionDetails.push(Object.assign(Object.assign({}, baton), { transmissionConfigurationDetails: response.data }));
                }
                else {
                    batonsWithTransmissionDetails.push(baton);
                }
            }
            catch (error) {
                batonsWithTransmissionDetails.push(baton);
            }
        });
        await Promise.all(promises);
        return batonsWithTransmissionDetails;
    };
    const getInitialData = async () => {
        var _a;
        try {
            setContext(prevContext => (Object.assign(Object.assign({}, prevContext), { isLoading: true })));
            const batonsCollection = await getBatons(accessToken, { fulfillmentGroupId: itemDetails.fulfillmentGroupId }, env === 'prd' ? BatonEnv.PRODUCTION : BatonEnv.INTEGRATION);
            const batonsWithExecutionDetails = await hydrateBatonsWithExecutionDetails(((_a = batonsCollection === null || batonsCollection === void 0 ? void 0 : batonsCollection._embedded) === null || _a === void 0 ? void 0 : _a.batons) || []);
            const batonsWithTransmissionDetails = await hydrateBatonsWithTransmissionDetails(batonsWithExecutionDetails);
            const batonV0List = await getAllV0BatonsForItem(itemDetails.fulfillmentGroupId, itemDetails.itemServiceUrl, accessToken, env);
            // Sort baton in orderlatest one first
            const sortedBatons = (batonsWithTransmissionDetails || [])
                .sort((a, b) => {
                return b.createdAt.localeCompare(a.createdAt);
            });
            setContext(prevContext => (Object.assign(Object.assign({}, prevContext), { batons: sortedBatons || [], batonsV0: batonV0List || [], isLoading: false })));
        }
        catch (error) {
            setContext(Object.assign(Object.assign({}, context), { batons: [], error: error.toString(), isLoading: false }));
        }
    };
    const isViewDetailButtonDisabled = (batonDetail) => {
        var _a;
        if ((batonDetail.status === BatonStatus.OPEN) || (batonDetail.status === BatonStatus.LOCKED) || (batonDetail.status === BatonStatus.PREPARING)) {
            return true;
        }
        // Disable view details for polling baton having a existing v0 legacy Batons
        if ((_a = batonDetail._links.legacyBatons) === null || _a === void 0 ? void 0 : _a.href) {
            return true;
        }
        switch (batonDetail.transmissionMethodKey) {
            case TransmissionMethodKeys.Email:
            case TransmissionMethodKeys.PromoStandards:
            case TransmissionMethodKeys.FileTransfer:
            case TransmissionMethodKeys.ApiPush:
            case TransmissionMethodKeys.SiteFlow:
            case TransmissionMethodKeys.ApiPoll:
                return false;
            default:
                return true;
        }
    };
    const getBatonLabelStatus = (batonStatus) => {
        switch (batonStatus) {
            case BatonStatus.CLOSE:
                return 'success';
            case BatonStatus.LOCKED:
                return 'info';
            case BatonStatus.PREPARING:
                return 'info';
            case BatonStatus.OPEN:
                return 'warning';
            case BatonStatus.FAILED:
                return 'danger';
            default:
                return 'info';
        }
    };
    const showRetransmitButton = (baton) => {
        switch (baton.transmissionMethodKey) {
            case TransmissionMethodKeys.Email: {
                const executions = baton.executionDetails;
                // eslint-disable-next-line dot-notation
                return !!executions.find(e => { var _a; return (_a = e.events) === null || _a === void 0 ? void 0 : _a.find(event => event['eventType'] === 'Bounce'); });
            }
            default:
                return baton.status === BatonStatus.FAILED;
        }
    };
    const getStatusLabel = (batonStatus, attempts) => {
        if (batonStatus === BatonStatus.CLOSE && attempts === 0) {
            return (_jsx(Label, { text: t('itemTransmission.batonStatus.unknown'), status: 'info' }));
        }
        return (_jsx(Label, { text: t(`itemTransmission.batonStatus.${batonStatus}`), status: getBatonLabelStatus(batonStatus) }));
    };
    const getTransmissionConfigurationColumnElement = (baton) => {
        var _a;
        if (!baton.transmissionConfigurationDetails) {
            return _jsx(Tooltip, Object.assign({ contents: t('itemTransmission.transmissionDetail.noDetails') }, { children: _jsx("em", Object.assign({ css: mutedTextCss }, { children: baton._links.transmissionConfiguration.href.split('/').at(-1) })) }));
        }
        return (_jsx("div", Object.assign({ style: { display: 'flex' } }, { children: _jsx("a", Object.assign({ href: `${supplierIntegrationEnvUrl}/tcs/${(_a = baton.transmissionConfigurationDetails) === null || _a === void 0 ? void 0 : _a.id}`, target: 'blank' }, { children: baton.transmissionConfigurationDetails.name })) })));
    };
    const getColumns = () => {
        const columns = [
            {
                Header: t('itemTransmission.tableHeader.actionType'),
                accessor: 'actionType',
                Cell(row) {
                    return isV0Baton(row.original)
                        ? t(`batonV0.actionType.${row.original.operation}.title`)
                        : t(`actionType.${row.original.actionType}.title`);
                }
            },
            {
                Header: t('itemTransmission.tableHeader.type'),
                accessor: 'transmissionMethodKey',
                Cell(row) {
                    return isV0Baton(row.original)
                        ? t(`batonV0.method.${row.original.method}.title`)
                        : t(`transmissionMethod.${row.original.transmissionMethodKey}.header`);
                }
            },
            {
                Header: t('itemTransmission.tableHeader.timePeriod'),
                accessor: 'createdAt',
                Cell(row) {
                    // TODO: Figure out how to pass the correct locale
                    const fmt1 = new Intl.DateTimeFormat('sv', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                        hour12: false,
                        timeZone: displayTimezone,
                        timeZoneName: 'short'
                    });
                    if (row.original.status === BatonStatus.CLOSE || row.original.status === BatonStatus.FAILED) {
                        return fmt1.formatRange(new Date(row.original.createdAt), new Date(row.original.modifiedAt
                            ? row.original.modifiedAt
                            : row.original.createdAt));
                    }
                    return `${formatUserDate(row.original.modifiedAt, displayTimezone)} - now`;
                }
            },
            {
                Header: t('itemTransmission.tableHeader.status'),
                accessor: 'status',
                Cell(row) {
                    return isV0Baton(row.original)
                        ? _jsx(Label, { text: t(`itemTransmission.batonStatus.${row.original.status}`), status: getBatonLabelStatus(row.original.status) })
                        : getStatusLabelForBatonV2Transmission(row.original);
                }
            },
            {
                Header: '',
                accessor: '',
                Cell(row) {
                    if (context.showDetails) {
                        return _jsx(_Fragment, {});
                    }
                    return (_jsx(Button, Object.assign({ size: 'sm', variant: 'link', disabled: isV0Baton(row.original) ? false : isViewDetailButtonDisabled(row.original), onClick: () => setContext(Object.assign(Object.assign({}, context), { showDetails: true, batonToFilter: row.original })) }, { children: t('itemTransmission.viewDetails') })));
                }
            }
        ];
        // Do not show Tc detail and Retransmit button for V0 Baton
        if (context.showDetails && !isV0Baton(context.batonToFilter)) {
            columns.push({
                Header: t('itemTransmission.tableHeader.transmissionConfigurationUsed'),
                accessor: 'transmissionMethodKey',
                Cell(row) {
                    return getTransmissionConfigurationColumnElement(row.original);
                }
            });
            if (showRetransmitButton(context.batonToFilter)) {
                columns.push({
                    Header: '',
                    accessor: '',
                    Cell(row) {
                        return _jsx(ReTransmit, { accessToken: accessToken, batonList: context.batons, batonToTransmit: context.batonToFilter, language: language, env: env, onClose: () => {
                                setContext(prev => (Object.assign(Object.assign({}, prev), { showDetails: false, batonToFilter: null, reTransmissionContext: { showReTransmissionContent: false } })));
                                getInitialData();
                            } });
                    }
                });
            }
        }
        return columns;
    };
    const getStatusLabelForBatonV2Transmission = (baton) => {
        var _a, _b, _c, _d;
        switch (baton.transmissionMethodKey) {
            case TransmissionMethodKeys.Email: {
                if ((_a = baton.executionDetails) === null || _a === void 0 ? void 0 : _a.length) {
                    const executions = baton.executionDetails;
                    if (!executions.find(e => (e.status === EmailStatus.Sent) || (e.status === EmailStatus.Running))) {
                        return _jsx(Label, { text: t('itemTransmission.batonStatus.failed'), status: 'warning' });
                    }
                    if (!executions.find(e => (e.status === EmailStatus.Sent)) && executions.find(e => (e.status === EmailStatus.Running))) {
                        return _jsx(Label, { text: t('itemTransmission.batonStatus.unknown'), status: 'info' });
                    }
                }
                return getStatusLabel(baton.status, baton.lockDetails.lockAttemptsMade);
            }
            case TransmissionMethodKeys.ApiPoll: {
                if ((_b = baton._links.legacyBatons) === null || _b === void 0 ? void 0 : _b.href) {
                    return _jsx(_Fragment, {});
                }
                return getStatusLabel(baton.status, (_c = baton.lockDetails) === null || _c === void 0 ? void 0 : _c.lockAttemptsMade);
            }
            default:
                return getStatusLabel(baton.status, (_d = baton.lockDetails) === null || _d === void 0 ? void 0 : _d.lockAttemptsMade);
        }
    };
    const onClose = () => {
        setContext(previousState => (Object.assign(Object.assign({}, previousState), { showDetails: false, batonToFilter: null, reTransmissionContext: { showReTransmissionContent: false }, error: { showError: false, errorMessage: '', batonTransmissionDetails: null } })));
    };
    const getTransmissionResponse = (baton) => {
        if (!isV0Baton(baton)) {
            switch (baton.transmissionMethodKey) {
                case TransmissionMethodKeys.PromoStandards:
                    return _jsx(PromoStandardsTransmissionDetails, { transmissionDetails: baton, onClose: onClose, language: language });
                case TransmissionMethodKeys.Email:
                    return _jsx(EmailTransmissionDetails, { accessToken: accessToken, transmissionDetails: baton, onClose: onClose, onError: onError, language: language, displayTimezone: displayTimezone });
                case TransmissionMethodKeys.FileTransfer:
                    return _jsx(FileTransferTransmissionDetails, { accessToken: accessToken, transmissionDetails: baton, onClose: onClose, onError: onError, language: language, displayTimezone: displayTimezone });
                case TransmissionMethodKeys.ApiPush:
                    return _jsx(ApiPushTransmissionDetails, { transmissionDetails: baton, onClose: onClose, language: language, accessToken: accessToken, onError: onError, displayTimezone: displayTimezone });
                case TransmissionMethodKeys.SiteFlow:
                    return _jsx(SiteFlowTransmissionDetails, { transmissionDetails: baton, language: language });
                case TransmissionMethodKeys.ApiPoll:
                    return _jsx(ApiPollTransmissionDetails, { transmissionDetails: baton, accessToken: accessToken, language: language });
                default:
                    return null;
            }
        }
        else {
            return _jsx(BatonV0TransmissionDetails, { baton: baton, language: language });
        }
    };
    const noBatonsFoundErrorMessage = _jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ css: marginBottom25 }, { children: [t('itemTransmission.noTransmissionPart1'), t('itemTransmission.noTransmissionPart2'), _jsx("a", Object.assign({ target: '_blank', href: "https://cimpress-support.atlassian.net/wiki/spaces/CI/pages/15111651492/Supplier+Integrations", rel: "noreferrer" }, { children: "Supplier Integration." }))] })), t('itemTransmission.transmissionProblem'), " ", _jsx("a", Object.assign({ href: "mailto:platformsupport@cimpress.com" }, { children: "platformsupport@cimpress.com" }))] });
    return _jsxs("div", { children: [context.showDetails
                ? _jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ css: cssBack }, { children: [_jsx(IconArrowLeftAlt, { weight: 'fill', size: '1x' }), _jsx("a", Object.assign({ role: 'button', onClick: onClose }, { children: t('itemTransmission.showAll') }))] })), _jsx("div", Object.assign({ css: detailsHeader }, { children: getColumns().map((c, i) => (_jsxs("div", { children: [_jsx("div", Object.assign({ className: 'si-f-title' }, { children: c.Header })), _jsx("div", { children: c.Cell ? c.Cell({ original: context.batonToFilter }) : 'n/a' })] }, i))) }))] })
                : context.isLoading
                    ? _jsx(Spinner, {})
                    : !context.batons.length && !context.batonsV0.length
                        ? _jsx(Alert, { status: 'warning', message: noBatonsFoundErrorMessage, dismissible: false })
                        : _jsx(Table, { data: [...context.batons, ...context.batonsV0], noDataText: t('itemTransmission.noData'), showPaginationBottom: true, columns: getColumns(), keyField: 'id', showPageSizeOptions: false, sortable: false, pageSize: (context.batons.length + context.batonsV0.length) }), (context.showDetails && context.batonToFilter && !context.error.showError)
                ? _jsx("div", Object.assign({ css: cssDetailsSection }, { children: getTransmissionResponse(context.batonToFilter) }))
                : (context.error.showError
                    && _jsx("div", Object.assign({ css: cssDetailsSection }, { children: _jsx(BatonTransmissionDetails, { transmissionDetails: context.error.batonTransmissionDetails, error: context.error.errorMessage, language: language }) })))] });
};
